import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {DeepReadonly} from 'utility-types';

import {defineMessage, Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {makeEnumTypeGuard} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {collectRecordEntries} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {
  StyledRequiredSpan,
  StyledTextLabel,
} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {TextBox} from '../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dropdown, DropdownProps} from '../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {
  DataFieldsTableColumnModel,
  DataFieldsTableColumnTypesEnum,
} from '../../models/api/dataFieldsTableColumnModel';
import {SingleChoiceDropdown} from '../../settings/rules/ruleEditor/dropdowns/singleChoiceDropdown';
import {DialogButtonGroup} from '../common/dialogs/dialogButtonGroup';
import {DialogContent} from '../common/dialogs/dialogContent';
import {DialogHeader} from '../common/dialogs/dialogHeader';
import {dataFieldsTableColumnTypeRenderData} from './helpers';

/*
 * Props.
 */

interface RuleEditorDataTableColumnDialogProps extends DropdownProps {
  mode: 'create' | 'edit';
  initialData?: DeepReadonly<Partial<DataFieldsTableColumnModel>>;
  onSubmit: HandlerOf<Partial<DataFieldsTableColumnModel>>;
  columnValidator?: RendererOf<Partial<DataFieldsTableColumnModel>>;
  onDelete?: Handler;
}

/*
 * Constants.
 */

const TYPE_DROPDOWN_CHOICES = collectRecordEntries(dataFieldsTableColumnTypeRenderData).map(
  ([id, entry]) => ({
    ...entry,
    id,
  }),
);

const isValidDataFieldsTableType = makeEnumTypeGuard(DataFieldsTableColumnTypesEnum);

/*
 * Styles.
 */

const StyledDropdownWrapper = styled.div`
  margin-top: 8px;
  > ${StyledTextLabel} {
    margin-bottom: 8px;
  }
`;

/*
 * Component.
 */

export const DataFieldsTableColumnDialog: FC<RuleEditorDataTableColumnDialogProps> = ({
  mode,
  onSubmit,
  onDelete,
  columnValidator,
  initialData = {},
  ...dialogProps
}) => {
  const [name, setName] = useState(initialData?.name ?? '');
  const [type, setType] = useState(initialData?.type ?? DataFieldsTableColumnTypesEnum.STRING);
  const localize = useLocalize();
  const errorMessage = columnValidator?.({name, id: initialData?.id});
  const hasError = Boolean(errorMessage);
  const theme = useUserTheme();

  return (
    <Dropdown {...dialogProps} width={300}>
      <DialogHeader
        title={
          mode === 'create' ? (
            <Trans id="rule-editor-data-table-add-column-dialog-title-create">Add column</Trans>
          ) : (
            <Trans id="rule-editor-data-table-add-column-dialog-title-edit">Edit column</Trans>
          )
        }
      />
      <DialogContent>
        <TextBox
          label={<Trans id="rule-editor-data-table-add-column-name-field-label">Name</Trans>}
          value={name}
          placeholder={localize(
            defineMessage({
              id: 'rule-editor-data-table-add-column-name-field-placeholder',
              message: 'Column name',
            }),
          )}
          onChange={(value) => setName(value)}
          isRequired
          noMargin
          shouldAutoFocus
          errorMessage={errorMessage}
          hasError={hasError}
        />

        <StyledDropdownWrapper>
          <StyledTextLabel $size={VisualSizesEnum.MEDIUM}>
            <Trans id="rule-editor-data-table-column-type-dropdown-label">Column type</Trans>
            <StyledRequiredSpan>*</StyledRequiredSpan>
          </StyledTextLabel>
          <SingleChoiceDropdown
            placeholder={
              <Trans id="rule-editor-data-table-column-type-dropdown-placeholder">Select a column type</Trans>
            }
            items={TYPE_DROPDOWN_CHOICES}
            getItemKey={({id}) => id}
            selectedItem={type}
            onChange={(newType) => isValidDataFieldsTableType(newType) && setType(newType)}
            renderItemLabel={({label}) => label}
            renderItemIcon={({icon, selectedIcon}, isInsideValue) => (
              <Icon
                name={isInsideValue ? selectedIcon : icon}
                color={isInsideValue ? theme.greys.shade50 : theme.greys.shade80}
              />
            )}
            serializeItemForSearch={() => ''}
            isSearchDisabled
          />
        </StyledDropdownWrapper>
      </DialogContent>
      <DialogButtonGroup>
        {mode === 'edit' && onDelete && (
          <Button
            variant={ButtonVariantsEnum.SECONDARY_DANGER}
            onClick={() => {
              onDelete();
              dialogProps.onRequestClose?.();
            }}
          >
            <Trans id="rule-editor-data-table-add-column-delete">Delete</Trans>
          </Button>
        )}
        <ButtonGroupSpacer />
        <Button variant={ButtonVariantsEnum.SECONDARY} onClick={() => dialogProps.onRequestClose?.()}>
          <Trans id="rule-editor-data-table-add-column-cancel">Cancel</Trans>
        </Button>
        <Button
          variant={ButtonVariantsEnum.PRIMARY}
          onClick={() => {
            onSubmit?.({name, type});
            dialogProps.onRequestClose?.();
          }}
          isDisabled={name === undefined || hasError}
        >
          {mode === 'create' ? (
            <Trans id="rule-editor-data-table-add-column-buttons-create">Create</Trans>
          ) : (
            <Trans id="rule-editor-data-table-add-column-buttons-edit">Save</Trans>
          )}
        </Button>
      </DialogButtonGroup>
    </Dropdown>
  );
};
